import { RoundState } from '../utils/roundUtils';

// Central source of truth for "what's the default next action in a live
// lesson?". Consumed by:
//   • the presentation-clicker hook (PageDown / Space / Enter)
//   • the orange "Next / Start activity / End activity / Close" buttons
//   • (planned) auto-start-next-activity timers
//
// Keep this module free of React imports — it stays a pure dispatch table
// so it's trivial to unit-test and reuse from anywhere in the app.

export type NextAction =
  | 'noop'
  | 'confirmEndPresentation'
  | 'viewReport'
  | 'closeStrengthReview'
  | 'nextStrengthResponse'
  | 'openFirstStrengthResponse'
  | 'seeResults'
  | 'startLesson'
  | 'startPressed'
  | 'endSlide'
  | 'openPassage'
  | 'closePassage'
  | 'showWordcloud'
  | 'skipForward';

// Inputs the decision needs. All boolean fields default false at the call
// site; treat undefined optionals as their absent semantics.
export interface NextActionState {
  // Lesson-level state
  roundState?: RoundState;
  isEndGameModalVisible: boolean;
  // game.isComplete — set after the teacher advances past the last slide.
  // While true, EndOfLessonOverlay is rendered ("you reached the end") and
  // the next clicker press should fire viewReport.
  isLessonComplete: boolean;
  showAIFeedbackOverlay: boolean;
  isAIFeedbackResultsSlide: boolean;
  canStartActivity: boolean;
  isFrozen: boolean;
  nextSlideType?: string;

  // Results-slide context (drives Stage 3: opening the strength review)
  isReviewResultsSlide: boolean;
  hasResponses: boolean;
  isGreatPicksGenerating: boolean;
  hasStrengthReviewBeenOpened: boolean;

  // AI-feedback scoreboard activity context. When a results slide is for an
  // AI-assessed activity that has a `score` prompt output AND a maxScore set,
  // the host walks scoreboard → feedback overlay → strength view instead of
  // overlay → scoreboard → strength view. `hasFeedbackOverlayBeenShown`
  // tracks whether the overlay has already been displayed for this activity
  // (sourced from the same feedback-given storage the widget already writes),
  // so the dispatcher knows to show it once before falling through to the
  // strength branch.
  isAiFeedbackScoreboardActive: boolean;
  hasFeedbackOverlayBeenShown: boolean;

  // Strength-review modal (Stages 4–6)
  isStrengthReviewOpen: boolean;
  // True when the next press should close the modal: either the teacher
  // is at/past the last top-pick response, or the AI generated zero
  // strengths (lastTopPickIndex === -1) so the modal has nothing to walk
  // through and the very first press closes it again.
  isStrengthReviewCloseAction: boolean;

  // Join lobby — the orange "Ready" button is showing. Once at least one
  // participant has joined, a clicker press should start the lesson.
  isShowingLobby: boolean;
  hasParticipantsJoined: boolean;

  // Attachment passage on a content slide. On non-activity slides
  // (type==='slide') with at least one attachmentPassage element, the
  // clicker walks each passage open → close before advancing. Activity
  // slides ignore this flow entirely.
  hasAttachmentElement: boolean;
  isPassageOpen: boolean;
  hasPassageBeenOpened: boolean;
  isActivitySlide: boolean;

  // The host-side "Show word cloud?" overlay is up on a finished
  // wordcloud activity. One clicker press should reveal the cloud
  // (consume the press); the next advances normally.
  isWordcloudOverlayVisible: boolean;
}

export interface NextActionHandlers {
  // Action buttons / lesson nav
  skipForward?: () => void;
  skipBack?: () => void;
  endPresentation?: () => void;
  // EndOfLessonOverlay's "View Report" — distinct metric/path from
  // endPresentation (which is the explicit-modal-confirm flow).
  viewReport?: () => void;

  // Activity flow (widget)
  startPressed?: () => void;
  endSlide?: () => void;
  seeResults?: () => void;

  // Join lobby — clicker equivalent of the "Ready" button.
  startLesson?: () => void;

  // Attachment passages — open the next unopened passage / close the
  // currently-open one. Activity slides do not register these.
  openPassage?: () => void;
  closePassage?: () => void;

  // Reveal the wordcloud on a finished wordcloud slide (closes the
  // "Show word cloud?" overlay; equivalent to clicking the orange
  // Ready button).
  showWordcloud?: () => void;

  // Strength review flow
  openFirstStrengthResponse?: () => void;
  nextStrengthResponse?: () => void;
  closeStrengthReview?: () => void;
}

// Actions that mutate observable lesson state — used by the clicker hook
// to drop a debounced double-tap that would otherwise fire two
// transitions in a row.
export const STATE_MUTATING_NEXT_ACTIONS: ReadonlySet<NextAction> = new Set<NextAction>([
  'confirmEndPresentation',
  'viewReport',
  'closeStrengthReview',
  'nextStrengthResponse',
  'openFirstStrengthResponse',
  'seeResults',
  'startLesson',
  'startPressed',
  'endSlide',
  'openPassage',
  'closePassage',
  'showWordcloud',
  'skipForward',
]);

// The decision function. Top-to-bottom, first match wins. Order is
// significant — if you reorder these branches you change behavior.
export function decideNextAction(state: NextActionState): NextAction {
  // 1. End-lesson confirm dialog (manually opened from the action bar).
  if (state.isEndGameModalVisible) return 'confirmEndPresentation';

  // 2. Lesson is complete — EndOfLessonOverlay is showing. Advance leads
  //    straight to the report. PageUp un-completes (handled by skipBack).
  if (state.isLessonComplete) return 'viewReport';

  // 3. Strength review modal walk-through. Comes before the AI overlay
  //    branch because the modal sits on top of the same results slide.
  if (state.isStrengthReviewOpen) {
    return state.isStrengthReviewCloseAction
      ? 'closeStrengthReview'
      : 'nextStrengthResponse';
  }

  // 4. AI feedback overlay (the blurred "Read the feedback on your
  //    device" splash). One press dismisses it and reveals strengths.
  if (state.showAIFeedbackOverlay && state.isAIFeedbackResultsSlide) {
    return 'seeResults';
  }

  // 5. Join lobby — clicker starts the lesson once a student has joined.
  //    With no students joined yet we consume the press (noop) so we
  //    don't fall through to the activity flow and advance off the lobby.
  if (state.isShowingLobby) {
    return state.hasParticipantsJoined ? 'startLesson' : 'noop';
  }

  // 6. Wordcloud activity finished and the "Show word cloud?" overlay is
  //    up — consume the press to reveal the cloud. The next press then
  //    flows through the activity-flow switch (seeResults / skipForward).
  if (state.isWordcloudOverlayVisible) {
    return 'showWordcloud';
  }

  // 7. Attachment passage on a content slide. Walk each passage open →
  //    close before advancing. Activity slides are skipped here so the
  //    activity-flow switch below stays unchanged for them.
  if (!state.isActivitySlide && state.hasAttachmentElement) {
    if (state.isPassageOpen) return 'closePassage';
    if (!state.hasPassageBeenOpened) return 'openPassage';
  }

  // 8. Activity flow.
  switch (state.roundState) {
    case 'pre':
      // canStartActivity is true only on the first slide of an activity
      // (getIsFirstSlideOfActivity returns false for type==='slide' and
      // for non-first activity slides). For everything else in 'pre' —
      // plain content slides, mid-chain activity slides we navigated
      // into directly — there's no activity to start, so advance.
      // handleSkipForward is activity-chain-aware and will skip past
      // related slides as needed.
      return state.canStartActivity ? 'startPressed' : 'skipForward';
    case 'countdown':
      return 'noop';
    case 'in':
      return state.isFrozen ? 'startPressed' : 'endSlide';
    case 'post-between':
      return state.nextSlideType !== 'voting' ? 'seeResults' : 'startPressed';
  }

  // 8.5. Scoreboard activity, sitting on the results slide. The flow is:
  //   scoreboard (visible by default) → feedback overlay → strength view.
  //   Once the overlay has been shown for this activity, this branch is
  //   inert and the press falls through to branch 9 (open strength).
  if (
    state.isAiFeedbackScoreboardActive &&
    !state.showAIFeedbackOverlay &&
    !state.hasFeedbackOverlayBeenShown &&
    state.hasResponses
  ) {
    return 'seeResults';
  }

  // 9. Results slide with responses available — open the strength review
  //    on the first response if we haven't done that yet. We deliberately
  //    do NOT gate on isGreatPicksGenerating: while strengths are still
  //    being detected the modal renders a loader (StrengthGeneratingLoader
  //    inside StrengthResponseReviewView), so the teacher sees something
  //    happen and can wait. Skipping past the modal would lose the chance
  //    to surface the picks once they arrive.
  if (
    state.isReviewResultsSlide &&
    state.hasResponses &&
    !state.hasStrengthReviewBeenOpened
  ) {
    return 'openFirstStrengthResponse';
  }

  // 10. Default: advance. handleSkipForward already detects the last slide
  //    and sets game.isComplete=true (which then routes future presses to
  //    viewReport above), so no special-case here.
  return 'skipForward';
}

export function executeNextAction(
  action: NextAction,
  handlers: NextActionHandlers,
): void {
  switch (action) {
    case 'confirmEndPresentation':
      handlers.endPresentation?.();
      return;
    case 'viewReport':
      handlers.viewReport?.();
      return;
    case 'closeStrengthReview':
      handlers.closeStrengthReview?.();
      return;
    case 'nextStrengthResponse':
      handlers.nextStrengthResponse?.();
      return;
    case 'openFirstStrengthResponse':
      handlers.openFirstStrengthResponse?.();
      return;
    case 'seeResults':
      handlers.seeResults?.();
      return;
    case 'startLesson':
      handlers.startLesson?.();
      return;
    case 'startPressed':
      handlers.startPressed?.();
      return;
    case 'endSlide':
      handlers.endSlide?.();
      return;
    case 'openPassage':
      handlers.openPassage?.();
      return;
    case 'closePassage':
      handlers.closePassage?.();
      return;
    case 'showWordcloud':
      handlers.showWordcloud?.();
      return;
    case 'skipForward':
      handlers.skipForward?.();
      return;
    case 'noop':
      return;
  }
}

// Tolgee key + default for the orange button label that corresponds to
// each action. UI components can call this so the button always shows
// the same verb the clicker would do.
export interface NextActionLabel {
  keyName: string;
  defaultValue: string;
}

export function getNextActionLabel(action: NextAction): NextActionLabel | undefined {
  switch (action) {
    case 'confirmEndPresentation':
      return {
        keyName: 'End Lesson & View Report',
        defaultValue: 'End Lesson & View Report',
      };
    case 'viewReport':
      return { keyName: 'View Report', defaultValue: 'View Report' };
    case 'closeStrengthReview':
      return { keyName: 'close', defaultValue: 'Close' };
    case 'nextStrengthResponse':
      return { keyName: 'Next', defaultValue: 'Next' };
    case 'openFirstStrengthResponse':
      return { keyName: 'Next', defaultValue: 'Next' };
    case 'seeResults':
      return { keyName: 'Next', defaultValue: 'Next' };
    case 'startLesson':
      return { keyName: 'Ready', defaultValue: 'Ready' };
    case 'startPressed':
      return { keyName: 'Start activity', defaultValue: 'Start activity' };
    case 'endSlide':
      return { keyName: 'End activity', defaultValue: 'End activity' };
    case 'openPassage':
      return { keyName: 'Open passage', defaultValue: 'Open passage' };
    case 'closePassage':
      return { keyName: 'close', defaultValue: 'Close' };
    case 'showWordcloud':
      return { keyName: 'Ready', defaultValue: 'Ready' };
    case 'skipForward':
      return { keyName: 'Next', defaultValue: 'Next' };
    case 'noop':
      return undefined;
  }
}

// The empty-state default. Use this anywhere you need a NextActionState
// shape but only some fields are known.
export const DEFAULT_NEXT_ACTION_STATE: NextActionState = {
  roundState: undefined,
  isEndGameModalVisible: false,
  isLessonComplete: false,
  showAIFeedbackOverlay: false,
  isAIFeedbackResultsSlide: false,
  canStartActivity: false,
  isFrozen: false,
  nextSlideType: undefined,
  isReviewResultsSlide: false,
  hasResponses: false,
  isGreatPicksGenerating: false,
  hasStrengthReviewBeenOpened: false,
  isAiFeedbackScoreboardActive: false,
  hasFeedbackOverlayBeenShown: false,
  isStrengthReviewOpen: false,
  isStrengthReviewCloseAction: false,
  isShowingLobby: false,
  hasParticipantsJoined: false,
  hasAttachmentElement: false,
  isPassageOpen: false,
  hasPassageBeenOpened: false,
  isActivitySlide: false,
  isWordcloudOverlayVisible: false,
};
